import React from "react";

export const EXPORT_FORMATS = {
  EXCEL: "excel",
  PDF: "pdf",
  DOC: "doc",
};

function sanitizeFilename(name) {
  return (
    String(name || "export")
      // eslint-disable-next-line no-control-regex
      .replace(/[<>:"/\\|?*\x00-\x1f]/g, "_")
      .trim() || "export"
  );
}

function escapeCsvCell(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function rowsToMatrix(columns, rows) {
  const header = columns.map((column) => column.label);
  const body = rows.map((row) =>
    columns.map((column) => {
      const value =
        typeof column.accessor === "function"
          ? column.accessor(row)
          : row[column.key];
      return value ?? "";
    }),
  );
  return { header, body };
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportTableToExcel({ columns, rows, filename, title }) {
  const { header, body } = rowsToMatrix(columns, rows);
  const csvLines = [
    ...(title ? [escapeCsvCell(title)] : []),
    header.map(escapeCsvCell).join(","),
    ...body.map((line) => line.map(escapeCsvCell).join(",")),
  ];
  const csv = `\uFEFF${csvLines.join("\n")}`;
  triggerDownload(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    `${sanitizeFilename(filename)}.csv`,
  );
}

export function exportTableToDoc({ columns, rows, filename, title }) {
  const { header, body } = rowsToMatrix(columns, rows);
  const tableHeader = header
    .map(
      (cell) =>
        `<th style="border:1px solid #d1d5db;padding:8px;background:#f3f4f6;text-align:left;font-size:12px;">${cell}</th>`,
    )
    .join("");
  const tableBody = body
    .map(
      (line) =>
        `<tr>${line
          .map(
            (cell) =>
              `<td style="border:1px solid #e5e7eb;padding:8px;font-size:12px;">${String(cell ?? "").replace(/</g, "&lt;")}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");

  const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><title>${title || filename}</title></head>
<body>
  ${title ? `<h2 style="font-family:Arial,sans-serif;color:#111827;">${title}</h2>` : ""}
  <table style="border-collapse:collapse;width:100%;font-family:Arial,sans-serif;">
    <thead><tr>${tableHeader}</tr></thead>
    <tbody>${tableBody}</tbody>
  </table>
</body>
</html>`;

  triggerDownload(
    new Blob([html], { type: "application/msword" }),
    `${sanitizeFilename(filename)}.doc`,
  );
}

export async function exportTableToPdf({ columns, rows, filename, title }) {
  const [{ pdf }, { default: TableExportPdfDocument }] = await Promise.all([
    import("@react-pdf/renderer"),
    import("@/shared/components/ui/Export/TableExportPdfDocument"),
  ]);

  const { header, body } = rowsToMatrix(columns, rows);
  const doc = React.createElement(TableExportPdfDocument, {
    title: title || filename,
    headers: header,
    rows: body,
  });
  const blob = await pdf(doc).toBlob();
  triggerDownload(blob, `${sanitizeFilename(filename)}.pdf`);
}

export async function exportTableData({
  format,
  columns,
  rows,
  filename,
  title,
}) {
  if (!rows?.length) {
    throw new Error("No data available to export.");
  }

  switch (format) {
    case EXPORT_FORMATS.EXCEL:
      exportTableToExcel({ columns, rows, filename, title });
      return;
    case EXPORT_FORMATS.DOC:
      exportTableToDoc({ columns, rows, filename, title });
      return;
    case EXPORT_FORMATS.PDF:
      await exportTableToPdf({ columns, rows, filename, title });
      return;
    default:
      throw new Error("Unsupported export format.");
  }
}
